import { create } from 'zustand'
import { combine, devtools } from 'zustand/middleware'

/* A search-focus keystroke held for an input that isn't mounted yet.
 *
 * `ShortcutSearchInput` binds its combo on mount, so the combo only works on a
 * surface already showing the field. The desk canvas is the case that breaks:
 * its search field lives in the *other* view, so ⌘⇧F (and the magnifier beside
 * the view switcher) has to swap the desk to list view first — and that view is
 * a lazy chunk over a Liveblocks room, so the field appears some indefinite time
 * later. There is nothing to focus at the moment the key is pressed.
 *
 * So the keystroke is held rather than replayed: the request outlives the
 * component that published it, and whichever `ShortcutSearchInput` claims the
 * same combo takes focus when it mounts. It is released when an input has
 * actually focused, not when one has rendered — the difference matters because
 * that mount can be torn down and rebuilt before it settles, which is exactly
 * what dropped the caret from a newly inserted sticky note. A one-shot
 * `autoFocus` on the first mount is lost to the rebuild; a held request is
 * re-consumed by whichever instance survives it.
 *
 * Keyed by the combo because that is what identifies the field to focus — it is
 * the key the user pressed, and the inputs already claim their own. A surface
 * bound to a different combo ignores a request that isn't its own. */
export const useSearchFocusRequestStore = create(
  devtools(
    combine({ requestedShortcut: null as string | null }, (set) => ({
      requestSearchFocus: (shortcut: string) =>
        set({ requestedShortcut: shortcut }),
      clearSearchFocusRequest: () => set({ requestedShortcut: null }),
    }))
  )
)
